import React, { JSX, useEffect, useState, useRef } from "react";
import { LuPanelLeftClose, LuPanelLeft } from "react-icons/lu";
import { MdDashboard, MdOutlineCases } from "react-icons/md";
import { IoChevronDownOutline } from "react-icons/io5";
import { FaSort, FaSortAlphaDown, FaSortAlphaUp, FaArchive, FaHistory } from "react-icons/fa";


import { NavLink, useParams, useNavigate } from "react-router";
import { useProjectsStore, useProjectsPage } from "../state/projects";
import { useModal } from "../context/ModalContext";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { BsBuilding } from 'react-icons/bs';
import { AiOutlinePlus } from 'react-icons/ai';
import { getVersion } from "../api/version";
import { getBadgeTailwindClasses, getRiskTailwindClasses } from '../styles/designSystem';
import { getHighestRiskLevel } from '../constants/project';
import { NewProjectForm } from './NewProjectForm';
import { SidebarUserArea } from './SidebarUserArea';
import { useMyAccess } from '../hooks/useMyAccess';
import { isSystemAdmin, canOwnProjects } from '../helpers/projectPermissions';
import { DOWNLOAD_LOGS_ENABLED } from '../config/featureFlags';

type NavInterface = {
  id?: string;
  link?: string;
  label: string;
  icon?: JSX.Element;
  children?: NavInterface[];
  type?: 'string';
  issueCount?: number;
  taskCount?: number;
  projectCount?: number;
}

const NAV:NavInterface[] = [
  {
    link: '/dashboard',
    label: 'Dashboard',
    icon: <MdDashboard />
  },
  {
    link: '/projects',
    label: 'Projects',
    icon: <MdOutlineCases />
  },
  {
    link: '/admin/download-logs',
    label: 'Download Log',
    icon: <FaHistory />
  },
  {
    link: '/archived',
    label: 'Archived',
    icon: <FaArchive />
  },
];

const getRandomIssueCount = () => Math.floor(Math.random() * 6);

// Function to count total tasks from all projects
const getTotalTasksCount = (projects: ProjectInterface[]) => {
  let totalTasks = 0;
  
  projects.forEach((project: ProjectInterface) => {
    // Count Project Plan items
    if (project.items) {
      totalTasks += project.items.length;
    }

    // Count Action Items
    if ((project as any).actionItems) {
      totalTasks += (project as any).actionItems.length;
    }
  });

  return totalTasks;
};

// Determine project color by highest risk (align with Map)
const getProjectColor = (project: ProjectInterface): string => {
  const itemRisks = project.items?.map(item => (item.risks || 'none').toLowerCase()) || [];
  const actionItemRisks = (project as any).actionItems?.map((item: any) => (item.priority || 'none').toLowerCase()) || [];
  const highestRisk = getHighestRiskLevel([...itemRisks, ...actionItemRisks]);
  const riskClasses = getRiskTailwindClasses(highestRisk?.toLowerCase() || 'none');
  return riskClasses.bg;
};

const getIssueColor = (count: number): string => {
  if (count >= 4) return 'bg-red-500';
  if (count >= 2) return 'bg-orange-400';
  if (count >= 1) return 'bg-yellow-400';
  return 'bg-green-500';
};

const extendNavWithProjects = (baseNav: NavInterface[], projects: ProjectInterface[], currentProject: string, sortBy: string = 'name', _showRooms: boolean = true) => {
  const tasksCount = getTotalTasksCount(projects);
  
  // Sort projects based on sortBy parameter
  const sortedProjects = [...projects].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'name_desc':
        return b.name.localeCompare(a.name);
      case 'status':
        return (a.status || '').localeCompare(b.status || '');
      case 'created_date':
        return new Date(b.start_date || '').getTime() - new Date(a.start_date || '').getTime();
      case 'created_date_desc':
        return new Date(a.start_date || '').getTime() - new Date(b.start_date || '').getTime();
      default:
        return a.name.localeCompare(b.name);
    }
  });
  
  return baseNav.map(navItem => {
    if (navItem.label === 'Tasks') {
      return {
        ...navItem,
        taskCount: tasksCount
      };
    }
    
    if (navItem.label === 'Projects') {
      return {
        ...navItem,
        projectCount: projects.length,
        children: sortedProjects.map(project => {
          // Get the project color class and convert it to issue count for existing logic
          const colorClass = getProjectColor(project);
          let issueCount = 0;
          if (colorClass === 'bg-green-500') issueCount = 0;
          else if (colorClass === 'bg-[#666]') issueCount = 1;
          else if (colorClass === 'bg-orange-400') issueCount = 2;
          else if (colorClass === 'bg-red-500') issueCount = 4;
          
          // Room navigation disabled — room detail view is not ready for production
          const flatRooms: NavInterface[] = [];

          return {
            label: project.name,
            link: `/projects/${project.id}`,
            issueCount: issueCount,
            ...(project.id === currentProject && flatRooms.length > 0 ? {children: flatRooms} : {}),
          };
        })
      }
    }

    return navItem;
  })
}

export const DashboardMenu = () => {
  // Auto-collapse on small screens
  const getInitialCollapsed = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      return true; // Always collapsed on mobile/tablet
    }
    return localStorage.getItem('collapsed') === 'true';
  };

  const [collapsed, setCollapsed] = useState(getInitialCollapsed);
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [showProjectSortDropdown, setShowProjectSortDropdown] = useState(false);
  const [projectSortBy, setProjectSortBy] = useState<string>('name');
  const [versionInfo, setVersionInfo] = useState({ build: '825', build_date: '', version: '' });
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const orgDropdownRef = useRef<HTMLDivElement>(null);
  const projectSortRef = useRef<HTMLDivElement>(null);
  const projects = useProjectsStore((state) => state.projects);
  const { projectId } = useParams();
  const { setShowCreateRoomModal, setCreateRoomModalProps } = useModal();
  const { loadProjectsPage } = useProjectsPage();
  const { isFeatureEnabled } = useFeatureFlag();
  const navigate = useNavigate();

  // Get siteId for current project
  const currentSiteId = projects.find(p => p.id === projectId)?.sites?.[0]?.site_id || '';

  // Handle responsive collapse on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(event.target as Node)) {
        setShowOrgDropdown(false);
      }
      if (projectSortRef.current && !projectSortRef.current.contains(event.target as Node)) {
        setShowProjectSortDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load the caller's global access, used only to gate the Download Log nav
  // entry to system-admins. A single boolean doesn't warrant a new context.
  const { myAccess } = useMyAccess();

  // Load version information from API
  useEffect(() => {
    const loadVersion = async () => {
      try {
        const version = await getVersion();
        setVersionInfo({
          build: version.build || '825',
          build_date: version.build_date || '',
          version: version.version || ''
        });
      } catch (error) {
        console.error('Failed to load version:', error);
        // Keep default fallback values
      }
    };

    loadVersion();
  }, []);

  // Listen for room creation/update events to refresh projects data
  useEffect(() => {
    const handleRoomChange = () => {
      console.log('🏠 Room change event received in DashboardMenu, refreshing projects...');
      loadProjectsPage(1).catch((error: any) => {
        console.error('❌ Failed to reload projects after room change:', error);
      });
    };

    window.addEventListener('roomCreated', handleRoomChange);
    window.addEventListener('roomUpdated', handleRoomChange);
    
    return () => {
      window.removeEventListener('roomCreated', handleRoomChange);
      window.removeEventListener('roomUpdated', handleRoomChange);
    };
  }, [loadProjectsPage]);

  const roomsEnabled = isFeatureEnabled('ROOMS_MANAGEMENT');
  const extendedNav = extendNavWithProjects(NAV, projects, projectId || '', projectSortBy, roomsEnabled)
    .filter(n =>
      (n.link !== '/admin/download-logs' || (DOWNLOAD_LOGS_ENABLED && isSystemAdmin(myAccess))) &&
      (n.link !== '/archived' || canOwnProjects(myAccess))
    );

  // Sort options for projects
  const projectSortOptions = [
    { value: 'name', label: 'Name (A-Z)', icon: <FaSortAlphaDown /> },
    { value: 'name_desc', label: 'Name (Z-A)', icon: <FaSortAlphaUp /> },
    { value: 'status', label: 'Status', icon: <FaSort /> },
    { value: 'created_date', label: 'Created Date (Newest)', icon: <FaSort /> },
    { value: 'created_date_desc', label: 'Created Date (Oldest)', icon: <FaSort /> },
  ];

  // Generate initials for project avatar
  const getInitials = (name: string) => {
    return name.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  };

  // Deterministic color for project avatar based on name
  const getAvatarColor = (name: string) => {
    const colors = [
      'bg-[#171717]', 'bg-emerald-600', 'bg-violet-600', 'bg-amber-600',
      'bg-rose-600', 'bg-cyan-600', 'bg-[#171717]', 'bg-teal-600',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  const sidebarFont = { fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' };

  return(
    <>
    <aside className={`dashboard-menu max-h-screen overflow-y-auto transition-all duration-200 ease-out ${collapsed ? 'w-[52px] sm:w-[72px]' : 'w-[260px]'} flex flex-col sticky left-0 top-0 shadow-[1px_0_0_rgba(0,0,0,0.06)]`} style={{ backgroundColor: '#fafafa' }}>
      {/* Header */}
      <div className={`sticky top-0 z-10 ${collapsed ? 'px-2 py-3' : 'px-5 py-4'}`} style={{ backgroundColor: '#fafafa' }}>
        <div className="flex items-center relative" ref={orgDropdownRef}>
          {!collapsed && (
            <NavLink to="/dashboard" className="flex items-center gap-3 group">
              <div className="w-8 h-8 rounded-lg bg-black flex items-center justify-center">
                <span className="text-white text-sm font-bold" style={sidebarFont}>F</span>
              </div>
              {isFeatureEnabled('ORGANIZATION_DROPDOWN') ? (
                <>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setShowOrgDropdown(!showOrgDropdown);
                    }}
                    className="flex items-center gap-1.5 text-[14px] font-semibold text-[#171717] hover:text-black transition-colors"
                    style={sidebarFont}
                  >
                    Mirapath, Inc.
                    <IoChevronDownOutline className={`w-3 h-3 text-[#888] transition-transform duration-200 ${showOrgDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  {showOrgDropdown && (
                    <div className="absolute top-10 left-0 w-64 bg-white rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.08)] border border-[#eaeaea] py-1 z-50">
                      <button
                        onClick={() => setShowOrgDropdown(false)}
                        className="w-full px-3 py-2 text-left hover:bg-[#fafafa] text-[#444] flex items-center gap-3 text-[13px] transition-colors"
                        style={sidebarFont}
                      >
                        <BsBuilding className="w-3.5 h-3.5 text-[#888]" />
                        <span>Sunbird, Inc. <span className={getBadgeTailwindClasses('dev')}>dev</span></span>
                      </button>
                      <button
                        onClick={() => setShowOrgDropdown(false)}
                        className="w-full px-3 py-2 text-left hover:bg-[#fafafa] text-[#444] flex items-center gap-3 text-[13px] transition-colors"
                        style={sidebarFont}
                      >
                        <AiOutlinePlus className="w-3.5 h-3.5 text-[#888]" />
                        <span>Create Organisation <span className={getBadgeTailwindClasses('dev')}>dev</span></span>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <span className="text-[14px] font-semibold text-[#171717]" style={sidebarFont}>
                  Finderz.AI
                </span>
              )}
            </NavLink>
          )}
          <button
            onClick={() => {
              localStorage.setItem('collapsed', String(!collapsed));
              setCollapsed(!collapsed);
            }}
            className={`text-[#888] hover:text-[#444] hover:bg-[#eaeaea] rounded-md p-1.5 transition-all duration-150 ${collapsed ? 'mx-auto' : 'ml-auto'}`}
          >
            {collapsed ? <LuPanelLeft className="w-4 h-4" /> : <LuPanelLeftClose className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto px-3">
        <nav>
          {/* Main nav */}
          <ul className="space-y-0.5">
            {extendedNav.map((n, idx) => (
              <React.Fragment key={n.link + n.label + idx}>
                <li>
                  {n.link ? (
                    <NavLink
                      to={n.link}
                      className={({ isActive }) =>
                        `flex items-center ${collapsed ? 'justify-center' : 'justify-between'} px-2.5 py-[7px] text-[13px] rounded-md transition-all duration-150
                        ${isActive
                          ? 'text-[#171717] font-medium bg-white shadow-[0_1px_3px_rgba(0,0,0,0.06)] border border-[#eaeaea]'
                          : 'text-[#666] hover:text-[#171717] hover:bg-white/60'}`
                      }
                      style={sidebarFont}
                      title={collapsed ? n.label : undefined}
                    >
                      <div className="flex items-center gap-2.5">
                        {n.icon && <span className="text-[16px] opacity-70">{n.icon}</span>}
                        {!collapsed && <span>{n.label}</span>}
                      </div>
                      <div className="flex items-center gap-1.5">
                        {!collapsed && n.taskCount !== undefined && (
                          <span className="text-[11px] font-medium px-1.5 py-px rounded-md text-[#666] bg-[#eaeaea]" style={sidebarFont}>
                            {n.taskCount}
                          </span>
                        )}
                        {!collapsed && n.projectCount !== undefined && (
                          <span className="text-[11px] font-medium px-1.5 py-px rounded-md text-[#666] bg-[#eaeaea]" style={sidebarFont}>
                            {n.projectCount}
                          </span>
                        )}
                        {!collapsed && n.label === 'Projects' && (
                          <div className="relative" ref={projectSortRef}>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowProjectSortDropdown(!showProjectSortDropdown);
                              }}
                              className="text-[#888] hover:text-[#444] hover:bg-[#eaeaea] rounded p-1 transition-all duration-150"
                              title="Sort projects"
                            >
                              <FaSort className="w-3 h-3" />
                            </button>
                            {showProjectSortDropdown && (
                              <div className="absolute top-7 right-0 mt-1 w-52 bg-white rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.08)] border border-[#eaeaea] py-1 z-50">
                                {projectSortOptions.map((option) => (
                                  <button
                                    key={option.value}
                                    onClick={() => {
                                      setProjectSortBy(option.value);
                                      setShowProjectSortDropdown(false);
                                    }}
                                    className={`w-full px-3 py-1.5 text-left hover:bg-[#fafafa] flex items-center gap-2.5 text-[13px] transition-colors duration-100 ${
                                      projectSortBy === option.value ? 'text-[#171717] font-medium bg-[#fafafa]' : 'text-[#666]'
                                    }`}
                                    style={sidebarFont}
                                  >
                                    <span className="opacity-50">{option.icon}</span>
                                    <span>{option.label}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </NavLink>
                  ) : (
                    <div
                      className={`flex items-center ${collapsed ? 'justify-center' : 'gap-2.5'} px-2.5 py-[7px] text-[13px] text-[#888]`}
                      style={sidebarFont}
                      title={collapsed ? n.label : undefined}
                    >
                      {n.icon && <span className="text-[16px]">{n.icon}</span>}
                      {!collapsed && n.label}
                    </div>
                  )}
                </li>

                {/* Project list */}
                {!collapsed && n.children && (
                  <>
                    <div className="h-px bg-[#eaeaea] mx-2 my-2" />
                    <ul className="space-y-0.5">
                      {n.label === 'Projects' && n.children.length === 0 ? (
                        <li>
                          <button
                            onClick={() => setShowNewProjectModal(true)}
                            className="flex items-center gap-2 px-2.5 py-[7px] text-[13px] text-[#888] hover:text-[#171717] hover:bg-white/60 rounded-md transition-all duration-150 w-full text-left"
                            style={sidebarFont}
                          >
                            <AiOutlinePlus className="w-3.5 h-3.5" />
                            <span>New Project</span>
                          </button>
                        </li>
                      ) : n.children.map((c:NavInterface) => (
                        <React.Fragment key={c.link + c.label}>
                          {c.label !== 'Default' && (
                            <li>
                              <NavLink
                                to={c.link || ''}
                                className={({ isActive }) =>
                                  `group flex items-center gap-2.5 px-2.5 py-[7px] text-[13px] rounded-md transition-all duration-150
                                  ${isActive
                                    ? 'text-[#171717] font-medium bg-white shadow-[0_1px_3px_rgba(0,0,0,0.06)] border border-[#eaeaea]'
                                    : 'text-[#666] hover:text-[#171717] hover:bg-white/60 border border-transparent'}`
                                }
                                style={sidebarFont}
                              >
                                {/* Project avatar */}
                                <div className={`w-6 h-6 rounded-md ${getAvatarColor(c.label)} flex items-center justify-center flex-shrink-0`}>
                                  <span className="text-white text-[9px] font-bold leading-none" style={sidebarFont}>{getInitials(c.label)}</span>
                                </div>
                                <span className="truncate flex-1">{c.label}</span>
                                {('issueCount' in c) && typeof c.issueCount === 'number' && !c.children && (
                                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${getIssueColor(c.issueCount)}`} />
                                )}
                              </NavLink>
                              {c.children && (
                                <ul className="ml-4 space-y-0.5 mt-0.5">
                                  {c.children.map((cc:NavInterface, idx) => (
                                    <React.Fragment key={cc.link + cc.label + idx}>
                                      {cc.label !== 'Default' && (
                                        <li>
                                          {cc.type === 'string' ? (
                                            <div className="flex items-center justify-between px-2.5 py-1 text-[12px] text-[#888]" style={sidebarFont}>
                                              <span>{cc.label}</span>
                                              <div className={`w-1.5 h-1.5 rounded-full ${getIssueColor(getRandomIssueCount())}`} />
                                            </div>
                                          ) : (
                                            <NavLink
                                              to={cc.link || ''}
                                              className={({ isActive }) =>
                                                `flex items-center gap-2 px-2.5 py-1 text-[12px] rounded-md transition-all duration-150
                                                ${isActive
                                                  ? 'text-[#171717] font-medium'
                                                  : 'text-[#888] hover:text-[#171717]'}`
                                              }
                                              style={sidebarFont}
                                            >
                                              <div className={`w-1 h-1 rounded-full flex-shrink-0 ${getIssueColor(getRandomIssueCount())}`} />
                                              <span>{cc.label}</span>
                                            </NavLink>
                                          )}
                                        </li>
                                      )}
                                    </React.Fragment>
                                  ))}
                                  {roomsEnabled && (
                                  <li>
                                    <button
                                      onClick={() => {
                                        setCreateRoomModalProps({
                                          projectId: projectId,
                                          siteId: currentSiteId
                                        });
                                        setShowCreateRoomModal(true);
                                      }}
                                      className="w-full px-2.5 py-1 text-[12px] text-[#888] hover:text-[#171717] transition-colors duration-150 text-left rounded-md flex items-center gap-2"
                                      style={sidebarFont}
                                    >
                                      <AiOutlinePlus className="w-3 h-3" />
                                      Add Room
                                    </button>
                                  </li>
                                  )}
                                </ul>
                              )}
                            </li>
                          )}
                        </React.Fragment>
                      ))}
                    </ul>
                  </>
                )}
              </React.Fragment>
            ))}
          </ul>
        </nav>
      </div>

      {/* User area */}
      <div className={`border-t border-[#eaeaea] ${collapsed ? 'px-2 py-2' : 'px-3 py-2'}`}>
        <SidebarUserArea collapsed={collapsed} />
      </div>

      {/* Footer */}
      <div className={`border-t border-[#eaeaea] ${collapsed ? 'px-2 py-2' : 'px-5 py-3'}`}>
        <div className={`text-[11px] text-[#999] tracking-wide ${collapsed ? 'text-center' : ''}`} style={sidebarFont}>
          {collapsed
            ? (versionInfo.version ? `v${versionInfo.version}` : '·')
            : `${versionInfo.version ? `v${versionInfo.version}` : ''}${versionInfo.build ? ` · ${versionInfo.build}` : ' · 825'}`}
        </div>
      </div>

    </aside>
    
    {showNewProjectModal && (
      <NewProjectForm 
        onClose={() => setShowNewProjectModal(false)} 
        onProjectCreated={async (projectId: string) => {
          // Navigate to the new project
          navigate(`/projects/${projectId}`);
          setShowNewProjectModal(false);
          // Reload projects
          await loadProjectsPage(1);
        }}
      />
    )}
    </>
  );
};
